import React, { useState } from 'react'
import { reduxForm } from 'redux-form'
import ReactMarkdown from 'markdown-to-jsx'

import Field from '@ynap/field'
import Checkbox from '@ynap/checkbox'
import BEMHelper from '@ynap/bem-helper'
import { Notification } from '@ynap/notification'
import { Button, SimpleAsyncButton } from '@ynap/button'
import { CombinedSelectWithCustomValue } from '@ynap/select'
import { emailInfoOverlay } from '@ynap/login-and-registration-form'
import { submitAndCollateErrorsPromise } from '@ynap/form-utils'

import collateErrors from './collateErrors'
import validate from './validate'
import { getTitleOptions } from '../../utils/getTitleOptions'

const bem = new BEMHelper('RSVPForm')

export const RSVPForm = (props) => {
    const {
        className,
        config,
        locale,
        messages,
        form,
        handleSubmit,
        userSummary,
        createFastUser,
        updateUserInfo,
        isUpdatingUserInfo,
        isCreatingFastUser,
        error,
        initialValues,
    } = props
    const [showEmailInfoOverlay, setShowEmailInfoOverlay] = useState(false)

    const discount15OffCountries = config?.head ? config?.head?.discount15OffCountries : config?.discount15OffCountries
    const eventKey = config.head.events?.maTrackingPartnerCode

    const submit = (values) => {
        if (userSummary) {
            const userPayload = {
                contextAttribute: [
                    ...(values?.maMarketingConsent === 'true'
                        ? [
                              {
                                  attributeName: 'maMarketingConsent',
                                  attributeValue: {
                                      value: values?.maMarketingConsent,
                                  },
                              },
                          ]
                        : []),
                    ...(eventKey
                        ? [
                              {
                                  attributeName: 'maTrackingPartnerCode',
                                  attributeValue: {
                                      value: eventKey,
                                  },
                              },
                          ]
                        : []),
                ],
            }

            const action = updateUserInfo(userPayload)
            return submitAndCollateErrorsPromise(action, values, props)
        }

        const action = createFastUser({
            ...values,
            maMarketingConsent: values.maMarketingConsent || 'false',
            maTrackingPartnerCode: eventKey,
        })

        return submitAndCollateErrorsPromise(action, values, props)
    }

    return (
        <form id={form} className={bem(null, null, className)} onSubmit={handleSubmit(submit)}>
            <Field
                component={CombinedSelectWithCustomValue}
                className={bem('title', initialValues?.personTitle ? 'disabled' : null)}
                name="personTitle"
                label={messages.events.personTitle()}
                emptyLabel={initialValues?.personTitle ? initialValues.personTitle : messages.events.selectTitle()}
                autoComplete="honorific-prefix"
                options={[...getTitleOptions(messages)]}
                disabled={!!initialValues?.personTitle}
            />
            <Field
                className={bem('firstName')}
                name="firstName"
                type="text"
                label={messages.events.firstName()}
                autoComplete="given-name"
                disabled={!!initialValues?.firstName}
            />
            <Field
                className={bem('lastName')}
                name="lastName"
                type="text"
                label={messages.events.lastName()}
                autoComplete="family-name"
                disabled={!!initialValues?.lastName}
            />
            <Field
                className={bem('email')}
                name="logonId"
                type={initialValues?.logonId ? 'text' : 'email'}
                label={messages.events.logonId()}
                disabled={!!initialValues?.logonId}
            />
            {messages.events.maMarketingConsent && (
                <div>
                    <Field
                        className={bem('marketing')}
                        name="maMarketingConsent"
                        component={Checkbox}
                        format={(value) => value === 'true'}
                        normalize={(value) => value.toString()}
                        onChange={() => {}}
                        onBlur={(e) => e.preventDefault()}
                    >
                        <label className={bem('marketingConsentText')} htmlFor="maMarketingConsent">
                            {messages.events?.maMarketingConsent({
                                is15OffCountry: discount15OffCountries?.includes(locale.country),
                            })}{' '}
                            {messages.events.discountDisclaimer && (
                                <ReactMarkdown
                                    options={{
                                        overrides: {
                                            a: {
                                                component: Button,
                                                props: {
                                                    className: bem('link'),
                                                    type: 'link',
                                                    component: 'a',
                                                    target: '_blank',
                                                    rel: 'noopener',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {messages.events.discountDisclaimer({ locale: locale.id })}
                                </ReactMarkdown>
                            )}
                        </label>
                    </Field>
                    <Button
                        type="link"
                        component="a"
                        target="_blank"
                        rel="noopener"
                        onClick={() => setShowEmailInfoOverlay(true)}
                        className={bem('emailLink')}
                    >
                        {messages.events.emailReadMore()}
                    </Button>
                </div>
            )}
            <ReactMarkdown
                className={bem('terms')}
                options={{
                    forceBlock: true,
                    overrides: {
                        a: (props) => {
                            const { href, children } = props

                            return (
                                <Button
                                    type="link"
                                    component="a"
                                    href={href}
                                    target="_blank"
                                    rel="noopener"
                                    className={bem('link')}
                                >
                                    {children}
                                </Button>
                            )
                        },
                    },
                }}
            >
                {messages.events.consent({ locale: locale.id })}
            </ReactMarkdown>
            <SimpleAsyncButton
                type="submit"
                className={bem('submit')}
                containerClass={bem('submitContainer')}
                loading={isUpdatingUserInfo || isCreatingFastUser}
                copy={messages.events.cta()}
            />
            {showEmailInfoOverlay && (
                <div className={bem('emailOverlay')}>
                    {emailInfoOverlay(messages, locale, () => setShowEmailInfoOverlay(false))}
                </div>
            )}
            {error ? <Notification type="error" message={error} className={bem('error')} /> : null}
        </form>
    )
}

export default reduxForm({
    form: 'rsvpForm',
    collateErrors,
    validate,
    enableReinitialize: true,
})(RSVPForm)
